import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { priceImportsEndpointIdentifier } from './use-get-price-import-runs-api';

/**
 * Remove an import run.
 *
 * The BE refuses committed and partially committed runs with a named reason —
 * they wrote real prices and the run is their audit trail — so the error toast
 * here is the whole UX for that case.
 */
type TData = { data: boolean };
type TError = AxiosError;
type TVariables = { runId: number };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useDeletePriceImportRunApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ runId }: TVariables) => {
    try {
      const { data } = await http.delete(`${priceImportsEndpointIdentifier}/${runId}`);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [priceImportsEndpointIdentifier],
    onSuccess: () => {
      toast({ description: 'Import removed', status: 'success' });
    },
    ...options,
  });
}
